"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { notification, Button, Tag, Space, Typography } from "antd";
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  selectRendezvousReminder,
  clearRendezvousReminder,
  setRendezvousReminder,
} from "../../appointments/redux/slices/rendezvousSlice";
import { getCurrentUser } from "../../authPatient/redux/thunks/authThunks";
import rendezvousSocket from "../../appointments/hooks/rendezvousSocket";
import { selectEffectiveUserId } from "../../sous-profile/redux/selectors/sousProfileSelectors";
import Cookies  from "js-cookie";

const secureConsole = {
  log: () => {},
  warn: () => {},
  error: () => {},
  group: () => {},
  groupEnd: () => {},
  debug: () => {},
};

function findNotifHolder() {
  return (
    document.querySelector(".ant-notification") ||
    document.querySelector("[class*='ant-notification']")
  );
}

function normalizeRDVReminder(r) {
  if (!r) return null;
  const raw = r?.reminder ? r.reminder : r;

  const rdvId = raw.rdvId ?? raw.id ?? raw.rendezvousId;
  const startTime = raw.startTime || raw.date || raw.time;
  const medecinId = raw.medecinId;

  if (!rdvId) return null;

  return {
    rdvId: Number(rdvId),
    startTime,
    medecinId,
    message: raw.message, // ✅ on met fallback via i18n dans le rendu
    _key: raw._key,
    raw,
  };
}

export default function RendezvousToastBridge() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  // ✅ reminder redux
  const reminder = useSelector(selectRendezvousReminder);

  // ✅ local user (pas selector auth)
  const [localUser, setLocalUser] = useState(null);
  const effectiveUserId = useSelector(selectEffectiveUserId);

  const userId = useMemo(() => {
    const u = localUser || {};
    return effectiveUserId || u?.externalId || u?.uuid || u?.id || u?._id || null;
  }, [effectiveUserId, localUser]);

  const { markSeen } = rendezvousSocket();

  const [api, contextHolder] = notification.useNotification();
  const lastKeyRef = useRef(null);

  /* =========================
   * Ensure current user loaded (via thunk)
   * ========================= */
  useEffect(() => {
    const token = Cookies.get("accessToken");

    secureConsole.log("[RendezvousToastBridge] user check", {
      tokenExists: !!token,
      hasLocalUser: !!localUser,
      userId,
    });

    if (!token) return;

    if (!localUser) {
      secureConsole.log("[RendezvousToastBridge] dispatch(getCurrentUser())");
      dispatch(getCurrentUser())
        .unwrap()
        .then((res) => {
          const u = res?.data ?? res;
          secureConsole.log("[RendezvousToastBridge] ✅ getCurrentUser result", u);
          setLocalUser(u || null);
        })
        .catch((e) => {
          secureConsole.warn("[RendezvousToastBridge] ❌ getCurrentUser failed", e);
        });
    }
  }, [dispatch, localUser, userId]);

  /* =========================
   * Watchdog logs (toutes les 5s)
   * ========================= */
  useEffect(() => {
    const timer = setInterval(() => {
      secureConsole.log("[RendezvousToastBridge][watchdog]", {
        userId,
        hasLocalUser: !!localUser,
        hasReminder: !!reminder,
        reminderKey: reminder?._key,
        holderNow: !!findNotifHolder(),
      });
    }, 5000);

    return () => clearInterval(timer);
  }, [userId, localUser, reminder]);

  /* =========================
   * Reminder logs
   * ========================= */
  useEffect(() => {
    secureConsole.log("[RendezvousToastBridge] reminder changed", {
      hasReminder: !!reminder,
      reminder,
    });
  }, [reminder]);

  const openToast = useCallback(
    (r) => {
      const norm = normalizeRDVReminder(r);

      secureConsole.log("[RendezvousToastBridge] openToast() called", {
        hasNorm: !!norm,
        userId,
        hasLocalUser: !!localUser,
        holderBefore: !!findNotifHolder(),
        rawReminder: r,
        normReminder: norm,
      });

      if (!norm) {
        secureConsole.warn("[RendezvousToastBridge] blocked: invalid reminder payload");
        return;
      }

      const st = norm.startTime ? new Date(norm.startTime) : null;

      const key =
        norm._key || `${norm.rdvId}:${st?.toISOString?.() || "no-time"}`;

      // dedupe
      if (lastKeyRef.current === key) {
        secureConsole.log("[RendezvousToastBridge] dedupe same key -> ignore", key);
        return;
      }
      lastKeyRef.current = key;

      const locale = i18n?.language || "fr";
      const messageText =
        norm.message || t("rendezvousToast.defaultMessage");

      api.open({
        key,

        message: (
          <Space size={8} align="center">
            <ClockCircleOutlined />
            <span style={{ fontWeight: 700 }}>
              {t("rendezvousToast.title")}
            </span>
          </Space>
        ),

        description: (
          <div style={{ minWidth: 320 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
              }}
            >
              <Typography.Text strong style={{ fontSize: 14 }}>
                {messageText}
              </Typography.Text>

              <Tag color="processing" style={{ marginInlineEnd: 0 }}>
                {t("rendezvousToast.tag.dueSoon")}
              </Tag>
            </div>

            <div
              style={{
                marginTop: 8,
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <Tag>
                {t("rendezvousToast.tag.rdv", { rdvId: norm.rdvId })}
              </Tag>

              {norm.medecinId ? (
                <Tag>
                  {t("rendezvousToast.tag.doctor", { doctorId: norm.medecinId })}
                </Tag>
              ) : null}

              <Tag icon={<ClockCircleOutlined />}>
                {st
                  ? st.toLocaleString(locale, {
                      weekday: "short",
                      day: "2-digit",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : t("rendezvousToast.timeUnknown")}
              </Tag>
            </div>

            {!userId && (
              <div style={{ marginTop: 10 }}>
                <Tag color="warning" style={{ whiteSpace: "normal" }}>
                  {t("rendezvousToast.sessionNotLoaded")}
                </Tag>
              </div>
            )}
          </div>
        ),

        duration: 0,
        placement: "topRight",
        getContainer: () => document.body,
        style: {
          zIndex: 2147483647,
          borderRadius: 14,
          boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
          padding: "12px 14px",
        },

        btn: (
          <Space>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              disabled={!userId}
              onClick={async () => {
                try {
                  secureConsole.log("[RendezvousToastBridge] Seen click", {
                    rdvId: norm.rdvId,
                    userId,
                  });
                  markSeen(norm.rdvId);
                } finally {
                  api.destroy(key);
                  dispatch(clearRendezvousReminder());
                }
              }}
            >
              {t("rendezvousToast.actions.seen")}
            </Button>

            <Button
              icon={<CloseCircleOutlined />}
              onClick={() => {
                secureConsole.log("[RendezvousToastBridge] Dismiss click", { key });
                api.destroy(key);
                dispatch(clearRendezvousReminder());
              }}
            >
              {t("rendezvousToast.actions.dismiss")}
            </Button>
          </Space>
        ),

        onClose: () => {
          secureConsole.log("[RendezvousToastBridge] onClose -> clear reminder", {
            key,
          });
          dispatch(clearRendezvousReminder());
        },
      });

      setTimeout(() => {
        secureConsole.log(
          "[RendezvousToastBridge] holder after open?",
          !!findNotifHolder()
        );
      }, 100);
    },
    [api, dispatch, userId, localUser, markSeen, t, i18n]
  );

  /* =========================
   * Main effect
   * ========================= */
  useEffect(() => {
    secureConsole.log("[RendezvousToastBridge] tick", {
      hasReminder: !!reminder,
      userId,
      holderNow: !!findNotifHolder(),
      reminderKey: reminder?._key,
    });

    if (!reminder) return;
    openToast(reminder);
  }, [reminder, userId, openToast]);

  /* =========================
   * (OPTIONNEL) Bouton test toast
   * ========================= */
  // ✅ à enlever après debug
  // const test = () => {
  //   dispatch(
  //     setRendezvousReminder({
  //       rdvId: 999,
  //       medecinId: 1,
  //       startTime: new Date().toISOString(),
  //       message: "TEST REMINDER",
  //       _key: `test:${Date.now()}`,
  //     })
  //   );
  // };

  return <>{contextHolder}</>;
}
